import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, CheckCircle2, Loader2 } from 'lucide-react';
import {
  aiApi,
  assetGroupsApi,
  authorizationsApi,
  burpJobsApi,
  consentApi,
  engagementsApi,
  internalAgentsApi,
  kaliJobsApi,
  runsApi,
  scanCapabilitiesApi,
  scopePoliciesApi,
  usersApi,
  zapJobsApi,
} from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Wizard, type WizardStep } from '../components/ui/wizard';
import { Input, Label, FieldHint } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { useAuth } from '../lib/auth-context';
import { cn } from '../lib/cn';
import type { AssetGroup, ScopePolicy, Target } from '../lib/types';

// ---- Constants ----

const QUICK_SCAN_GROUP_NAME = 'Quick Scan Targets';
const DEFAULT_SCAN_DAYS = 30;

/** Every day, all day — Quick Scan is an immediate, one-off authorized test, not a recurring schedule. */
const ALWAYS_OPEN_SCAN_WINDOW = [{ days: [0, 1, 2, 3, 4, 5, 6], startMinute: 0, endMinute: 1440, tzOffsetMinutes: 0 }];

const SELF_ATTESTATION_LABEL =
  'I confirm I am authorized to test this target and take full responsibility for ensuring I have the legal right to do so.';

/** No dedicated `self_attested` value exists in AuthorizationMethod (packages/shared/src/authorization
 * — CODEOWNERS-gated, @bui/security) — adding one would need a security-reviewed schema change across
 * that package, the API's submitProof discriminated union, and every web-side consumer. `account_owner`
 * + a clearly-labeled evidenceRef already captures the same fact for the audit trail without touching
 * that gated schema; the literal string below is what a reviewer/auditor actually sees. */
const SELF_ATTESTATION_EVIDENCE_REF =
  'authorization_method: self_attested — user checked the Quick Scan self-attestation checkbox, confirming authorization and taking full responsibility for ensuring the legal right to test this target.';

interface ModuleInfo {
  id: string;
  label: string;
}

/** From packages/pentest-modules — see docs/modules.md. All 10 have a real probe wired in
 * apps/worker/src/index.ts. Discovery is the original fast 4-check subset; Full runs every module. */
const DISCOVERY_MODULES: ModuleInfo[] = [
  { id: 'external.weak-tls', label: 'Weak TLS / certificate hygiene' },
  { id: 'external.open-port-exposure', label: 'Open port exposure' },
  { id: 'external.config-exposure', label: 'Config / file exposure' },
  { id: 'external.session-auth-hygiene', label: 'Session / auth cookie hygiene' },
];

const ADDITIONAL_MODULES: ModuleInfo[] = [
  { id: 'external.web-crawl-mapping', label: 'Web crawl mapping' },
  { id: 'external.api-schema-ingestion', label: 'API schema ingestion' },
  { id: 'external.subdomain-takeover-risk', label: 'Subdomain takeover risk' },
  { id: 'external.safe-injection-signature', label: 'Injection signature detection' },
  { id: 'external.safe-ssrf-oob', label: 'SSRF (out-of-band)' },
  { id: 'external.authz-idor-bola', label: 'Authz IDOR/BOLA' },
];

const ALL_MODULES: ModuleInfo[] = [...DISCOVERY_MODULES, ...ADDITIONAL_MODULES];

/** Phase 4 — dispatched via apps/kali (BUI-1019) on top of the normal module run, external zone.
 * A deliberately curated subset of KALI_TOOLS (packages/shared/src/kali/tool-allowlist.ts), not
 * every allowlisted tool — same "considered subset" posture as the allowlist itself. */
const RED_TEAM_KALI_TOOLS = ['nmap', 'nikto', 'whatweb', 'gobuster', 'nuclei'] as const;
/** Phase 4 — dispatched via apps/kali, internal zone, against an internal target only. */
const INTERNAL_RED_TEAM_KALI_TOOLS = ['ldapdomaindump', 'enum4linux-ng', 'kerbrute'] as const;

type ScanProfile = 'discovery' | 'full' | 'redTeam' | 'internalRedTeam' | 'endpointHardening' | 'custom';
const SCAN_PROFILES: readonly ScanProfile[] = ['discovery', 'full', 'redTeam', 'internalRedTeam', 'endpointHardening', 'custom'];

/** Display labels matching ProfileCard's own titles (ProfileStep, below) — used by the Scan Scope
 * Advisor pill (TargetStep) to show a human-readable name for the AI's recommendedProfile, which
 * comes back as the raw ScanProfile identifier (e.g. "redTeam"). */
const PROFILE_LABEL: Record<ScanProfile, string> = {
  discovery: 'Discovery',
  full: 'Vulnerability Assessment',
  redTeam: 'Red Team',
  internalRedTeam: 'Internal Red Team',
  endpointHardening: 'Endpoint Hardening',
  custom: 'Custom',
};

/**
 * Validates the wizard's `?profile=` pre-fill (Internal Testing's capability "Run" dialogs — see
 * InternalTesting.tsx) against the known ScanProfile union — an unrecognized/absent value falls
 * back to `'full'`, the wizard's own pre-existing default, rather than trusting an arbitrary query
 * string straight into component state.
 */
export function parseScanProfileParam(value: string | null): ScanProfile {
  return SCAN_PROFILES.includes(value as ScanProfile) ? (value as ScanProfile) : 'full';
}

// ---- Pure helpers (exported for testing) ----

const IPV4_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/;

/**
 * Strips an optional scheme + any path/query/fragment, trims whitespace — mirrors
 * packages/queue/src/planning.ts's normalizeDomainValue() exactly. Without this, a user pasting
 * a full URL ("http://testfire.net") instead of a bare domain flowed straight through as the
 * scope policy's domain entry, which the API rejects as invalid (a URL isn't a valid domain), and
 * as the target's stored value, which would then need the SAME stripping again downstream. This
 * normalizes once, at entry, so every API call in this wizard already sends the bare domain.
 */
export function normalizeDomainValue(value: string): string {
  const trimmed = value.trim();
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  return withoutScheme.split(/[/?#]/)[0] ?? withoutScheme;
}

const IPV4_CIDR_PATTERN = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;

/**
 * Like `normalizeDomainValue`, but leaves a bare CIDR range (e.g. "192.168.1.0/24" — Internal
 * Testing's "Start Scan" dialog accepts either a single IP or a CIDR) untouched instead of
 * truncating at the "/": `normalizeDomainValue`'s path/query/fragment stripping only makes sense
 * for a domain or a pasted URL, never for CIDR notation typed directly. `normalizeDomainValue`
 * itself is intentionally left alone (it mirrors packages/queue/src/planning.ts's own function
 * exactly) — this is a separate, additive wrapper, not a behavior change to the original.
 */
function normalizeTargetValue(value: string): string {
  const trimmed = value.trim();
  return IPV4_CIDR_PATTERN.test(trimmed) ? trimmed : normalizeDomainValue(value);
}

export function detectTargetKind(value: string): 'domain' | 'ip' {
  const normalized = normalizeTargetValue(value);
  return IPV4_PATTERN.test(normalized) || IPV4_CIDR_PATTERN.test(normalized) ? 'ip' : 'domain';
}

/**
 * Pure RFC 1918 check (10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16) — no network call, since this
 * only needs to run against a literal IPv4 address the user already typed in. Domain-kind targets
 * are NOT checked here: client-side DNS resolution isn't reliable from a browser (no `dns`
 * module, and a same-origin fetch wouldn't reveal the resolved IP either), so a domain that
 * happens to resolve to a private address is instead caught server-side at scan-kickoff/planning
 * time via the existing `internal` scope-zone mechanism (packages/queue/src/planning.ts's
 * `planValidationJobs`, which already plans an `internal.service_check` job per in-scope internal
 * target). This wizard's client-side check only ever handles the literal-IP case — a reasonable,
 * honest scope boundary rather than a fake DNS check that would just be wrong some of the time.
 * Also accepts an optional CIDR suffix ("192.168.1.0/24") — checks the network address's octets
 * and validates the prefix length is in range (0-32); a bare IP is unaffected.
 */
export function isPrivateIPv4(ip: string): boolean {
  const [address, prefix] = ip.trim().split('/');
  if (prefix !== undefined) {
    if (!/^\d{1,2}$/.test(prefix) || Number(prefix) > 32) return false;
  }
  const octets = (address ?? '').split('.');
  if (octets.length !== 4) return false;
  const parsed = octets.map((o) => Number(o));
  if (parsed.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return false;
  const [a, b] = parsed as [number, number, number, number];
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return false;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIsoDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface ExistingTargetMatch {
  target: Target;
  assetGroup: AssetGroup;
}

/** Searches every asset group's targets for an exact value match — there's no tenant-wide target
 * search endpoint, so this fans out across assetGroupsApi.targets() the same way Targets.tsx does. */
export async function findExistingTarget(value: string): Promise<ExistingTargetMatch | null> {
  const { assetGroups } = await assetGroupsApi.list();
  const normalized = value.trim().toLowerCase();
  for (const group of assetGroups) {
    const { targets } = await assetGroupsApi.targets(group.id);
    const match = targets.find((t) => t.value.toLowerCase() === normalized);
    if (match) return { target: match, assetGroup: group };
  }
  return null;
}

function buildScopePolicy(kind: 'domain' | 'ip', value: string, zone: ScopePolicy['zone'] = 'external'): ScopePolicy {
  return {
    zone,
    allow: {
      cidrs: kind === 'ip' ? [value] : [],
      domains: kind === 'domain' ? [value] : [],
      hosts: [],
      ports: [],
      paths: [],
    },
    deny: { cidrs: [], domains: [], hosts: [], ports: [], paths: [] },
    allowUnlistedPorts: false,
  };
}

/** Tags which orchestration step failed, so the UI can say e.g. "Couldn't authorize this target"
 * instead of a generic error — friendlyErrorMessage(err.cause) still supplies the underlying reason. */
export class QuickScanStepError extends Error {
  constructor(
    public readonly step:
      | 'target'
      | 'engagement'
      | 'consent'
      | 'scope'
      | 'authorization'
      | 'verification'
      | 'activation'
      | 'run'
      | 'agentJob'
      | 'redTeamJobs',
    public readonly cause: unknown,
  ) {
    super(step);
  }
}

async function runStep<T>(step: QuickScanStepError['step'], fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw new QuickScanStepError(step, err);
  }
}

export interface QuickScanInput {
  targetValue: string;
  targetKind: 'domain' | 'ip';
  existingTarget: ExistingTargetMatch | null;
  notBefore: string;
  notAfter: string;
  authorizedBy: { name: string; email: string };
  /** Base64-encoded signed authorization document, read client-side via FileReader — optional. */
  consentDocumentBase64?: string;
  /**
   * Set only when the Target step detected a literal RFC 1918 IP (`isPrivateIPv4`) and the user
   * picked (or was auto-assigned, when exactly one was connected) a connected internal agent to
   * run it. Triggers dispatching a `port-scan` job to that agent right after the run starts.
   */
  internalAgentId?: string;
  /** Phase 4 — 'redTeam'/'internalRedTeam' additionally dispatch Kali/Burp jobs after the run
   * starts (see runQuickScan below); 'discovery'/'full'/'custom' only ever affect the profile
   * step's own display (module selection isn't actually threaded through to the run yet — a
   * pre-existing gap, not one this change introduces or fixes). 'endpointHardening' never reaches
   * runQuickScan at all — see QuickScan()'s handleFinish. Optional (defaults to no extra
   * dispatch, i.e. same behavior as 'full') so existing callers/tests that predate Phase 4 don't
   * need updating just to keep compiling. */
  profile?: ScanProfile;
}

/** The full behind-the-scenes sequence BUI-985 asks for, adapted to the API surface that actually
 * exists (see the 5-part report for what changed vs. the ticket's assumed endpoints):
 *   ensure target -> create engagement -> create scope policy -> create authorization ->
 *   submit proof -> activate -> start run.
 * Each step is tagged with QuickScanStepError so the caller can report exactly what failed. Not
 * resumable — a retry after a failure restarts from the top; see the report for why. */
export async function runQuickScan(input: QuickScanInput): Promise<{ runId: string }> {
  // Defense in depth: normalize here too, not just at the wizard's one call site — every API call
  // below must send the bare domain, never a pasted-in scheme/path, regardless of what a future
  // caller passes in.
  const targetValue = normalizeTargetValue(input.targetValue);

  await runStep('target', async () => {
    if (input.existingTarget) return;
    const { assetGroups } = await assetGroupsApi.list();
    let group = assetGroups.find((g) => g.zone === 'external' && g.name === QUICK_SCAN_GROUP_NAME);
    if (!group) {
      const created = await assetGroupsApi.create({ name: QUICK_SCAN_GROUP_NAME, zone: 'external' });
      group = created.assetGroup;
    }
    await assetGroupsApi.addTarget(group.id, { kind: input.targetKind, value: targetValue });
  });

  const { engagement } = await runStep('engagement', () =>
    engagementsApi.create({ name: `Quick Scan — ${targetValue}`, scanWindows: ALWAYS_OPEN_SCAN_WINDOW }),
  );

  // Legal consent gate (BUI-989) — must exist before the engagement's first run can start
  // (enforced server-side too, see apps/api/src/routes/runs.ts). The wizard's Authorize step
  // only lets the user reach here once all three checkboxes are checked, so they're always true.
  await runStep('consent', () =>
    consentApi.create(engagement.id, {
      checkboxes: { authorizedToTest: true, termsAccepted: true, liabilityAcknowledged: true },
      ...(input.consentDocumentBase64 ? { documentBase64: input.consentDocumentBase64 } : {}),
    }),
  );

  await runStep('scope', () => scopePoliciesApi.create(engagement.id, buildScopePolicy(input.targetKind, targetValue)));

  // BUG FIX: the Red Team profiles dispatch jobs into zones ('app' for Burp, 'internal' for the
  // internal Kali tools) that the 'external' scope policy above does not cover. Every dispatch
  // route re-runs the full preflight gate (kill switch -> authorization -> scan window -> scope)
  // for the job's OWN zone before creating it — with no scope policy for that zone,
  // runPreflightFromDb fails closed with MISSING_SCOPE_POLICY (a 403), which surfaced to users as
  // "Couldn't dispatch the Red Team scan jobs." Creating the matching-zone policy here, before any
  // job dispatch below, is what that gate is actually checking for.
  if (input.profile === 'redTeam') {
    await runStep('scope', () => scopePoliciesApi.create(engagement.id, buildScopePolicy(input.targetKind, targetValue, 'app')));
  } else if (input.profile === 'internalRedTeam') {
    await runStep('scope', () => scopePoliciesApi.create(engagement.id, buildScopePolicy(input.targetKind, targetValue, 'internal')));
  }

  // Every Quick Scan target is now self-attested (see SELF_ATTESTATION_EVIDENCE_REF's docstring for
  // why this reuses `account_owner` rather than a new method value) — the wizard's Target step only
  // lets the user reach here once the self-attestation checkbox is checked.
  const { authorization } = await runStep('authorization', () =>
    authorizationsApi.create(engagement.id, {
      method: 'account_owner',
      notBefore: new Date(input.notBefore).toISOString(),
      notAfter: new Date(`${input.notAfter}T23:59:59`).toISOString(),
      authorizedBy: input.authorizedBy,
    }),
  );

  await runStep('verification', () =>
    authorizationsApi.submitProof(authorization.id, { method: 'account_owner', evidenceRef: SELF_ATTESTATION_EVIDENCE_REF }),
  );
  await runStep('activation', () => authorizationsApi.activate(authorization.id));

  const { run } = await runStep('run', () => runsApi.start(engagement.id, input.profile));

  // Internal-target dispatch (BUI-1015 follow-up, "Task 1B"): the wizard only ever sets
  // `internalAgentId` once a connected agent is selected/auto-selected, so this is additive to the
  // external-scope-policy flow above, not a replacement for it — an internal IP target still gets
  // its own external-zone scope policy/authorization exactly like any other Quick Scan target;
  // this just ALSO queues a port-scan job for the agent to actually reach it. `targetValue` is
  // already a bare CIDR when the user entered a range (Internal Testing's "Start Scan" dialog) —
  // only a single IP needs the implicit /32 appended.
  if (input.internalAgentId) {
    const cidr = targetValue.includes('/') ? targetValue : `${targetValue}/32`;
    await runStep('agentJob', () =>
      internalAgentsApi.createJob(input.internalAgentId!, { type: 'port-scan', payload: { cidr }, runId: run.id }),
    );
  }

  // Phase 4 Red Team profiles: dispatched via the already-built Kali/Burp/ZAP closed-loop
  // infrastructure (BUI-1019/BUI-1020/BUI-1033) ON TOP OF the normal module run above, not instead
  // of it. Each Kali tool is its own dispatch (apps/api/src/routes/kali.ts requires one tool per
  // job) — a failure partway through surfaces as a single 'redTeamJobs' step error; jobs already
  // dispatched keep running independently (this wizard doesn't roll them back).
  if (input.profile === 'redTeam') {
    const target = input.targetKind === 'ip' ? { ip: targetValue } : { host: targetValue };
    await runStep('redTeamJobs', async () => {
      for (const tool of RED_TEAM_KALI_TOOLS) {
        await kaliJobsApi.dispatch({ runId: run.id, tool, zone: 'external', target });
      }
      // BUI-1033: ZAP (free, no license) is the default web scanner — Burp Suite Pro is used ONLY
      // when its own license is genuinely configured (apps/api/src/azure/scan-capabilities.ts's
      // webScanner field), never guessed from the wizard's own state. Checked fresh right before
      // dispatch, same "re-verify at the moment it matters" posture as this platform's preflight
      // re-checks elsewhere — not cached from whatever ProfileStep's display query last saw.
      const { webScanner } = await scanCapabilitiesApi.get();
      if (webScanner.tool === 'burp') {
        await burpJobsApi.dispatch({ runId: run.id, scanProfile: 'balanced', zone: 'app', urls: [`https://${targetValue}`] });
      } else {
        await zapJobsApi.dispatch({ runId: run.id, zone: 'app', urls: [`https://${targetValue}`] });
      }
    });
  } else if (input.profile === 'internalRedTeam') {
    // Requires an internal agent — the profile step only allows selecting this profile once one
    // is connected (mirrors the existing internal-target agent gate on the Target step).
    // BUI-1045: agentId routes these tools to run ON that connected agent (Kali's own Azure
    // Container Apps Job has no network path to an RFC1918 target) — required server-side for
    // zone:'internal' dispatch, not merely descriptive.
    const target = input.targetKind === 'ip' ? { ip: targetValue } : { host: targetValue };
    await runStep('redTeamJobs', async () => {
      for (const tool of INTERNAL_RED_TEAM_KALI_TOOLS) {
        await kaliJobsApi.dispatch({ runId: run.id, tool, zone: 'internal', target, agentId: input.internalAgentId });
      }
    });
  }

  return { runId: run.id };
}

const STEP_ERROR_MESSAGE: Record<QuickScanStepError['step'], string> = {
  target: "Couldn't save this target.",
  engagement: "Couldn't create the engagement.",
  consent: "Couldn't record your consent to the terms.",
  scope: "Couldn't set scope for this target.",
  authorization: "Couldn't create the authorization.",
  verification: "Couldn't verify ownership of this target.",
  activation: "Couldn't activate the authorization (only an Admin or Owner can do this).",
  run: "Couldn't start the scan.",
  agentJob: "Couldn't dispatch the scan to the internal agent.",
  redTeamJobs: "Couldn't dispatch the Red Team scan jobs.",
};

// ---- Step 1: Target ----

/** A connected agent, narrowed down to just what the selector needs to render — kept separate
 * from the full `InternalAgent` type so TargetStep doesn't need to import it just for this. */
interface ConnectedAgentOption {
  id: string;
  hostname: string;
}

function InternalTargetNotice({
  agentsLoading,
  connectedAgents,
  selectedAgentId,
  setSelectedAgentId,
}: {
  agentsLoading: boolean;
  connectedAgents: ConnectedAgentOption[];
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-xl border border-warning/40 bg-warning-soft px-4 py-3 text-sm">
      <p className="font-medium text-ink">Internal target detected</p>
      {agentsLoading ? (
        <p className="text-ink-muted">Checking for connected internal agents…</p>
      ) : connectedAgents.length === 0 ? (
        <p className="text-danger">
          No internal agent is connected — install and connect one on the{' '}
          <a href="/devices" className="font-medium underline underline-offset-2">
            Devices
          </a>{' '}
          page before scanning an internal address.
        </p>
      ) : connectedAgents.length === 1 ? (
        <p className="text-ink-muted">This scan will run via agent "{connectedAgents[0]!.hostname}".</p>
      ) : (
        <div className="space-y-1">
          <Label htmlFor="quick-scan-agent-select">Choose which agent runs this scan</Label>
          <select
            id="quick-scan-agent-select"
            value={selectedAgentId ?? ''}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface-sunken px-3 py-2 text-sm text-ink"
          >
            <option value="" disabled>
              Select an agent
            </option>
            {connectedAgents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.hostname}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function TargetStep({
  value,
  onChange,
  onBlurTarget,
  existingTarget,
  lookupPending,
  authorizedSelfAttest,
  setAuthorizedSelfAttest,
  isInternalTarget,
  agentsLoading,
  connectedAgents,
  selectedAgentId,
  setSelectedAgentId,
  scopeAdvice,
  onSelectRecommendedProfile,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Scan Scope Advisor (BUI-1019 follow-up): fires the AI recommendation lookup once the user
   * finishes editing this field, not on every keystroke. */
  onBlurTarget: () => void;
  existingTarget: ExistingTargetMatch | null;
  lookupPending: boolean;
  authorizedSelfAttest: boolean;
  setAuthorizedSelfAttest: (v: boolean) => void;
  isInternalTarget: boolean;
  agentsLoading: boolean;
  connectedAgents: ConnectedAgentOption[];
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string) => void;
  /** Scan Scope Advisor pill state — only the fields this component actually renders, not the
   * whole `useMutation` object, so this prop's type doesn't have to fight react-query's generics. */
  scopeAdvice: { isPending: boolean; data: Awaited<ReturnType<typeof aiApi.scopeAdvice>> | undefined };
  onSelectRecommendedProfile: (profile: ScanProfile) => void;
}) {
  const kind = detectTargetKind(value);
  // Narrowed once here (rather than inline in the JSX below) so the button's onClick closure gets
  // a plain `{ recommendedProfile: ScanProfile; rationale: string }` instead of TypeScript losing
  // the AiFeatureError-vs-success narrowing across the closure boundary.
  const advice =
    scopeAdvice.data && !('error' in scopeAdvice.data) && SCAN_PROFILES.includes(scopeAdvice.data.recommendedProfile as ScanProfile)
      ? { recommendedProfile: scopeAdvice.data.recommendedProfile as ScanProfile, rationale: scopeAdvice.data.rationale }
      : null;

  return (
    // Was implicitly narrowed by the page's own now-removed `max-w-2xl` wrapper (see QuickScan's
    // return below) — kept explicit here instead, so this step's fields render at the exact same
    // width as before regardless of the page shell now being full-width for Step 3's card grid.
    <div className="max-w-2xl space-y-5">
      <div className="space-y-1">
        <Label htmlFor="quick-scan-target">Domain or IP address</Label>
        <Input
          id="quick-scan-target"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlurTarget}
          placeholder="app.example.com"
          autoFocus
        />
        <FieldHint>{value ? `Detected as ${kind === 'ip' ? 'an IP address' : 'a domain'}.` : 'Enter a single domain or IPv4 address.'}</FieldHint>
      </div>

      {/* Scan Scope Advisor — fired on blur (see onBlurTarget), never on page load/keystroke.
          Silent on failure (no error text/toast): this is an assistive suggestion, not a primary
          feature, so a flaky/unconfigured backend just means no pill appears. */}
      {scopeAdvice.isPending ? (
        <p className="flex items-center gap-1.5 text-xs text-ink-muted">
          <Loader2 size={12} className="animate-spin" />
          Analysing target...
        </p>
      ) : advice ? (
        <button
          type="button"
          onClick={() => onSelectRecommendedProfile(advice.recommendedProfile)}
          className="w-full rounded-xl border border-brand-border bg-brand-subtle px-3 py-2 text-left text-xs text-brand-hover hover:bg-brand-subtle/80"
        >
          💡 Recommended: {PROFILE_LABEL[advice.recommendedProfile]} — {advice.rationale}
        </button>
      ) : null}

      {lookupPending ? <p className="text-xs text-ink-muted">Checking your workspace for this target…</p> : null}

      {existingTarget ? (
        <div className="flex items-center gap-2 rounded-xl bg-brand-subtle px-3 py-2 text-sm text-brand-hover">
          <CheckCircle2 size={16} />
          Already in your workspace (asset group "{existingTarget.assetGroup.name}") — pre-selected.
        </div>
      ) : null}

      {isInternalTarget ? (
        <InternalTargetNotice
          agentsLoading={agentsLoading}
          connectedAgents={connectedAgents}
          selectedAgentId={selectedAgentId}
          setSelectedAgentId={setSelectedAgentId}
        />
      ) : null}

      <label className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={authorizedSelfAttest}
          onChange={(e) => setAuthorizedSelfAttest(e.target.checked)}
        />
        <span className="font-medium text-ink">{SELF_ATTESTATION_LABEL}</span>
      </label>
    </div>
  );
}

// ---- Step 2: Authorize ----

/** Strips the `data:...;base64,` prefix FileReader.readAsDataURL() adds — the API only wants the raw base64 payload. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function AuthorizeStep({
  targetValue,
  confirmAuthorized,
  setConfirmAuthorized,
  acceptTerms,
  setAcceptTerms,
  acknowledgeLiability,
  setAcknowledgeLiability,
  notBefore,
  setNotBefore,
  notAfter,
  setNotAfter,
  documentFileName,
  onDocumentSelected,
}: {
  targetValue: string;
  confirmAuthorized: boolean;
  setConfirmAuthorized: (v: boolean) => void;
  acceptTerms: boolean;
  setAcceptTerms: (v: boolean) => void;
  acknowledgeLiability: boolean;
  setAcknowledgeLiability: (v: boolean) => void;
  notBefore: string;
  setNotBefore: (v: string) => void;
  notAfter: string;
  setNotAfter: (v: string) => void;
  documentFileName: string | null;
  onDocumentSelected: (file: File | null) => void;
}) {
  return (
    // See TargetStep's identical comment above — explicit max-w-2xl to preserve this step's
    // width now that the page-level wrapper providing it implicitly is gone.
    <div className="max-w-2xl space-y-5">
      <label className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={confirmAuthorized}
          onChange={(e) => setConfirmAuthorized(e.target.checked)}
        />
        <span className="font-medium text-ink">
          I confirm I am authorized to test {targetValue || 'this target'} and have written permission from the asset owner.
        </span>
      </label>

      <label className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm">
        <input type="checkbox" className="mt-0.5" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
        <span className="font-medium text-ink">
          I have read and accept BUI's{' '}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-brand underline underline-offset-2">
            Security Testing Terms &amp; Conditions
          </a>
          .
        </span>
      </label>

      <label className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={acknowledgeLiability}
          onChange={(e) => setAcknowledgeLiability(e.target.checked)}
        />
        <span className="font-medium text-ink">
          I understand that BUI (Pty) Ltd accepts no liability for any service disruption, data loss, or system
          instability resulting from this security test.
        </span>
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="quick-scan-not-before">Authorization starts</Label>
          <Input id="quick-scan-not-before" type="date" value={notBefore} onChange={(e) => setNotBefore(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="quick-scan-not-after">Authorization ends</Label>
          <Input id="quick-scan-not-after" type="date" value={notAfter} onChange={(e) => setNotAfter(e.target.value)} min={notBefore} />
        </div>
      </div>
      {notAfter < notBefore ? <p className="text-xs text-danger">End date must be on or after the start date.</p> : null}

      <div className="space-y-1">
        <Label htmlFor="quick-scan-document">Upload signed authorization document (optional)</Label>
        <input
          id="quick-scan-document"
          type="file"
          accept="application/pdf"
          onChange={(e) => onDocumentSelected(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-xl file:border file:border-border file:bg-surface-raised file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink"
        />
        {documentFileName ? <FieldHint>{`Attached: ${documentFileName}`}</FieldHint> : null}
      </div>
    </div>
  );
}

// ---- Step 3: Scan profile ----

/** Custom is the only card that keeps its real per-module checkbox list (see its ProfileCard
 * below) — everything else shows a single "N checks" summary line instead (no module names, no
 * per-check badges). Only this many checkbox rows are visible before the list's own fixed-height
 * `overflow-hidden` wrapper clips the rest; every module still renders (still a real, toggleable
 * checkbox, still individually selectable) — only the ones past this count are visually clipped,
 * with a "+ N more" line making clear there's more below. */
const CUSTOM_VISIBLE_CHECKS = 5;

function ProfileCard({
  title,
  description,
  selected,
  onSelect,
  children,
  duration,
  disabled,
  disabledReason,
  noMaxHeight,
}: {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  children?: ReactNode;
  duration?: string;
  disabled?: boolean;
  disabledReason?: string;
  /** Custom only, and only while its "Show all" toggle is expanded (see ProfileStep) — lets that
   * one card grow past the shared 220px cap so all 10 checkboxes can render. Every other card
   * (and Custom itself while collapsed) keeps the cap; this defaults to false/unset for them. */
  noMaxHeight?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      aria-pressed={selected}
      title={disabled ? disabledReason : undefined}
      className={cn(
        'relative flex h-full flex-col gap-1.5 overflow-hidden rounded-2xl border-2 p-3 text-left transition-colors',
        noMaxHeight ? undefined : 'max-h-[220px]',
        disabled
          ? 'cursor-not-allowed border-border bg-surface-sunken opacity-50'
          : selected
            ? 'border-brand bg-brand-subtle'
            : 'border-border bg-surface-sunken hover:bg-surface-raised',
      )}
    >
      {selected ? (
        <span
          aria-hidden="true"
          className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white"
        >
          <Check size={14} strokeWidth={3} />
        </span>
      ) : null}
      <div className="flex items-center gap-2 pr-6">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {duration ? (
          <Badge variant="neutral" className="px-1.5 py-0.5 text-[10px]">
            {duration}
          </Badge>
        ) : null}
      </div>
      <p className="line-clamp-2 text-xs text-ink-muted">{description}</p>
      {disabled && disabledReason ? <p className="text-xs italic text-ink-faint">{disabledReason}</p> : null}
      {children}
    </button>
  );
}

/** Replaces the old per-module "Live" list on every card except Custom — just how many checks
 * this profile runs, not which ones. */
function CheckCountLine({ count }: { count: number }) {
  return (
    <p className="mt-1 text-xs text-ink-muted">
      {count} check{count === 1 ? '' : 's'}
    </p>
  );
}

/** Conceptual capability areas per profile — a middle ground between the old full module-name
 * list and the bare "N checks" count: what the scan covers, not which individual modules/tools
 * implement it. Custom is deliberately excluded (it keeps its own real per-module checkbox list
 * below); every other profile caps at 6 (2 columns x 3 rows) to keep cards within the shared
 * 220px height cap — see ProfileStep's manual verification note. */
const PROFILE_TAGS: Partial<Record<ScanProfile, readonly string[]>> = {
  discovery: ['TLS hygiene', 'Port exposure', 'File exposure', 'Session security'],
  full: ['TLS hygiene', 'Port exposure', 'Web crawl', 'API schema', 'Injection detection', 'Auth/IDOR'],
  redTeam: ['All VA checks', 'Kali deep scan', 'ZAP web scan', 'Port & service', 'Tech fingerprint', 'Dir discovery'],
  internalRedTeam: ['AD enumeration', 'SMB & NetBIOS', 'Kerberos detection'],
  endpointHardening: ['Privilege escalation', 'Credential exposure', 'Lateral movement'],
};

/** A compact 2-column pill grid of a profile's capability areas — sits between the description
 * and the "N checks" count line on every card except Custom. */
function CapabilityTags({ tags }: { tags: readonly string[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-1.5">
      {tags.map((tag) => (
        <span key={tag} className="rounded-full border border-border-subtle bg-surface px-2 py-0.5 text-[11px] font-medium text-ink-muted">
          {tag}
        </span>
      ))}
    </div>
  );
}

function ProfileStep({
  profile,
  setProfile,
  customModuleIds,
  toggleCustomModule,
  internalRedTeamAvailable,
}: {
  profile: ScanProfile;
  setProfile: (p: ScanProfile) => void;
  customModuleIds: Set<string>;
  toggleCustomModule: (id: string) => void;
  /** Internal Red Team needs a connected internal agent (an internal-zone target reachable via a
   * broker/agent) — same requirement as the Target step's own internal-target agent gate. */
  internalRedTeamAvailable: boolean;
}) {
  // Collapsed (default): Custom's checkbox list shows only the first CUSTOM_VISIBLE_CHECKS
  // modules and that card keeps the shared 220px cap. Expanded: every module renders (all 10
  // stay real, toggleable checkboxes either way — this only changes how many are visible at
  // once) and the cap is lifted for Custom specifically, so the card grows to fit them; the row
  // below shifts down as a result, which is expected for this deliberate user action.
  const [customExpanded, setCustomExpanded] = useState(false);

  // Fetches once this step actually mounts (Wizard only renders the active step's content — see
  // components/ui/wizard.tsx), satisfying "fetches on Step 3 load" without a manual effect/gate.
  // Still needed even though the per-tool "Live"/"Not configured" badges are gone from these
  // cards (see CheckCountLine) — Red Team's description below still names the resolved web
  // scanner, and runQuickScan's own dispatch re-checks this independently at dispatch time.
  const capabilitiesQuery = useQuery({ queryKey: ['scanCapabilities'], queryFn: () => scanCapabilitiesApi.get() });
  // BUI-1033: ZAP (free) is the default web scanner; Burp Suite Pro only when actually licensed —
  // see apps/api/src/azure/scan-capabilities.ts's webScanner field. Defaults to 'zap' while still
  // loading/unconfigured so the card never flashes a "Burp" label it can't back up.
  const webScannerTool = capabilitiesQuery.data?.webScanner?.tool ?? 'zap';
  const webScannerLabel = webScannerTool === 'burp' ? 'Burp Suite Pro scan' : 'ZAP scan';
  // Red Team runs every Vulnerability Assessment module PLUS the Kali tools PLUS the web scanner —
  // the count line reflects everything this profile actually executes, not just the module count.
  const redTeamCheckCount = ALL_MODULES.length + RED_TEAM_KALI_TOOLS.length + 1;

  return (
    <div className="space-y-3">
      <div className="grid w-full grid-cols-3 items-stretch gap-3">
        <ProfileCard
          title="Discovery"
          description="4 fast checks: TLS, ports, config exposure, session hygiene."
          selected={profile === 'discovery'}
          onSelect={() => setProfile('discovery')}
        >
          <CapabilityTags tags={PROFILE_TAGS.discovery!} />
          <CheckCountLine count={DISCOVERY_MODULES.length} />
        </ProfileCard>
        <ProfileCard
          title="Vulnerability Assessment"
          description="All 10 checks — the most thorough option."
          selected={profile === 'full'}
          onSelect={() => setProfile('full')}
        >
          <CapabilityTags tags={PROFILE_TAGS.full!} />
          <CheckCountLine count={ALL_MODULES.length} />
        </ProfileCard>
        <ProfileCard
          title="Custom"
          description="Choose exactly which checks to run."
          selected={profile === 'custom'}
          onSelect={() => setProfile('custom')}
          noMaxHeight={customExpanded}
        >
          {/* Every module below is a real, toggleable checkbox, whether or not it's currently
              shown — collapsed just renders fewer of them (not clipped-but-present, so there's no
              hidden/unreachable checkbox the way an overflow-hidden clip would leave one). */}
          <ul className="mt-1 space-y-1">
            {(customExpanded ? ALL_MODULES : ALL_MODULES.slice(0, CUSTOM_VISIBLE_CHECKS)).map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 text-xs">
                <label className="flex items-center gap-2 text-ink">
                  <input
                    type="checkbox"
                    checked={customModuleIds.has(m.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleCustomModule(m.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {m.label}
                </label>
                <Badge variant="success" className="px-1.5 py-0.5 text-[10px]">
                  Live
                </Badge>
              </li>
            ))}
          </ul>
          {ALL_MODULES.length > CUSTOM_VISIBLE_CHECKS ? (
            // A real <button> here would nest inside ProfileCard's own outer <button> — invalid
            // HTML (browsers/React both flag nested buttons; content after the inner one can end
            // up outside any button at all once real markup gets parsed, not just constructed via
            // React's direct DOM calls). role="button" + tabIndex/onKeyDown gets the same
            // click-and-keyboard-activatable behavior, and is still queryable via
            // getByRole('button') in tests, without that risk.
            <span
              role="button"
              tabIndex={0}
              className="cursor-pointer text-xs text-ink-faint underline-offset-2 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setCustomExpanded(!customExpanded);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                e.stopPropagation();
                setCustomExpanded(!customExpanded);
              }}
            >
              {customExpanded ? 'Show less ↑' : `+ ${ALL_MODULES.length - CUSTOM_VISIBLE_CHECKS} more checks`}
            </span>
          ) : null}
        </ProfileCard>
      </div>
      <div className="grid w-full grid-cols-3 items-stretch gap-3">
        <ProfileCard
          title="Red Team"
          description={`Everything in Vulnerability Assessment, plus Kali deep scanning + a ${webScannerLabel}.`}
          selected={profile === 'redTeam'}
          onSelect={() => setProfile('redTeam')}
          duration="30-60 min"
        >
          <CapabilityTags tags={PROFILE_TAGS.redTeam!} />
          <CheckCountLine count={redTeamCheckCount} />
        </ProfileCard>
        <ProfileCard
          title="Internal Red Team"
          description="AD enumeration + Kerberos detection via your connected internal agent."
          selected={profile === 'internalRedTeam'}
          onSelect={() => setProfile('internalRedTeam')}
          duration="15-30 min"
          disabled={!internalRedTeamAvailable}
          disabledReason="Requires an internal (RFC 1918) target with a connected internal agent."
        >
          <CapabilityTags tags={PROFILE_TAGS.internalRedTeam!} />
          <CheckCountLine count={INTERNAL_RED_TEAM_KALI_TOOLS.length} />
        </ProfileCard>
        <ProfileCard
          title="Endpoint Hardening"
          description="Runs via your installed endpoint agents, not this scan — privilege escalation, credential exposure, and lateral-movement checks."
          selected={profile === 'endpointHardening'}
          onSelect={() => setProfile('endpointHardening')}
          duration="2-5 min"
        >
          <CapabilityTags tags={PROFILE_TAGS.endpointHardening!} />
        </ProfileCard>
      </div>
    </div>
  );
}

// ---- Page ----

export function QuickScan() {
  const navigate = useNavigate();
  const { principal } = useAuth();
  const { toast } = useToast();
  // Pre-fill support for Internal Testing's "Start Scan"/capability "Run" dialogs
  // (?target=<ip-or-cidr>&agent=<id>&profile=<ScanProfile>) — read once on mount only; this
  // wizard's own state is the source of truth from then on, so editing the target/agent/profile
  // afterward never fights with a stale URL value.
  const [searchParams] = useSearchParams();

  const [targetValue, setTargetValue] = useState(() => searchParams.get('target') ?? '');
  const [existingTarget, setExistingTarget] = useState<ExistingTargetMatch | null>(null);
  const [lookupPending, setLookupPending] = useState(false);
  const [authorizedSelfAttest, setAuthorizedSelfAttest] = useState(false);

  const [confirmAuthorized, setConfirmAuthorized] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acknowledgeLiability, setAcknowledgeLiability] = useState(false);
  const [notBefore, setNotBefore] = useState(todayIsoDate);
  const [notAfter, setNotAfter] = useState(() => addDaysIsoDate(DEFAULT_SCAN_DAYS));
  const [documentBase64, setDocumentBase64] = useState<string | null>(null);
  const [documentFileName, setDocumentFileName] = useState<string | null>(null);

  const [profile, setProfile] = useState<ScanProfile>(() => parseScanProfileParam(searchParams.get('profile')));
  const [customModuleIds, setCustomModuleIds] = useState<Set<string>>(() => new Set(ALL_MODULES.map((m) => m.id)));

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(() => searchParams.get('agent'));

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });
  const authorizedBy = useMemo(() => {
    const me = usersQuery.data?.users.find((u) => u.userId === principal?.userId);
    if (!me?.email || !me.name) return null;
    return { name: me.name, email: me.email };
  }, [usersQuery.data, principal?.userId]);

  const kind = detectTargetKind(targetValue);
  const isInternalTarget = kind === 'ip' && isPrivateIPv4(normalizeTargetValue(targetValue));

  // Internal-target awareness (BUI-1015 follow-up, "Task 1B"): an internal IP needs a connected
  // agent to actually reach it (see internalAgentsApi.list, already built for Internal Testing).
  const internalAgentsQuery = useQuery({ queryKey: ['internal-agents-quickscan'], queryFn: () => internalAgentsApi.list() });
  const connectedAgents = useMemo(
    () => (internalAgentsQuery.data?.agents ?? []).filter((a) => a.status === 'connected'),
    [internalAgentsQuery.data],
  );

  // Auto-select the sole connected agent; clear the selection whenever the target stops being
  // internal (e.g. the user edits it back to a domain or a public IP) or the prior selection no
  // longer corresponds to a currently-connected agent. Functional setState throughout so
  // `selectedAgentId` doesn't need to be a dependency — avoids clobbering a still-valid manual
  // multi-agent selection on every unrelated re-render.
  //
  // Skipped entirely while `internalAgentsQuery` is still loading: `connectedAgents` is `[]`
  // before the query resolves, and the "prune to only a still-connected id" branch below would
  // otherwise immediately null out a pre-selected `?agent=` id (Internal Testing's "Start Scan"
  // dialog) before the real agent list ever had a chance to confirm it's still valid.
  useEffect(() => {
    if (internalAgentsQuery.isLoading) return;
    if (!isInternalTarget) {
      setSelectedAgentId(null);
      return;
    }
    if (connectedAgents.length === 1) {
      setSelectedAgentId(connectedAgents[0]!.id);
      return;
    }
    setSelectedAgentId((prev) => (prev && connectedAgents.some((a) => a.id === prev) ? prev : null));
  }, [isInternalTarget, connectedAgents, internalAgentsQuery.isLoading]);

  const internalRedTeamAvailable = isInternalTarget && Boolean(selectedAgentId);

  // Same "don't leave a now-invalid selection standing" posture as the selectedAgentId effect
  // above — if the user picked Internal Red Team, then edited the target away from an internal
  // one (or its agent disconnected), fall back to Full rather than leaving an unselectable profile
  // chosen (the profile card is disabled, but a stale `profile` value would still be sent to
  // runQuickScan since ProfileStep's `disabled` is purely a UI affordance, not a state guard).
  useEffect(() => {
    if (profile === 'internalRedTeam' && !internalRedTeamAvailable) {
      setProfile('full');
    }
  }, [profile, internalRedTeamAvailable]);

  // Auto-check whether this target already exists, debounced.
  useEffect(() => {
    const normalized = normalizeTargetValue(targetValue);
    if (normalized.length < 3) {
      setExistingTarget(null);
      return;
    }
    let cancelled = false;
    setLookupPending(true);
    const timer = setTimeout(() => {
      findExistingTarget(normalized)
        .then((match) => {
          if (!cancelled) setExistingTarget(match);
        })
        .finally(() => {
          if (!cancelled) setLookupPending(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [targetValue]);

  // Scan Scope Advisor (UI 5) — gated on the same platform-wide AI-capabilities flag every other
  // new AI feature checks; hides the pill entirely (never fires) when OPENROUTER_API_KEY isn't
  // configured, rather than firing a call that will only ever degrade.
  const aiCapabilitiesQuery = useQuery({ queryKey: ['ai-capabilities'], queryFn: () => aiApi.capabilities() });
  const aiEnabled = aiCapabilitiesQuery.data?.enabled ?? false;
  const scopeAdviceMutation = useMutation({ mutationFn: () => aiApi.scopeAdvice({ targetUrl: normalizeTargetValue(targetValue) }) });
  // Avoids re-firing for the same value on every refocus/blur, and lets a fresh edit clear a
  // now-stale recommendation for a DIFFERENT target rather than leaving the old pill showing.
  const lastAnalyzedTargetRef = useRef<string | null>(null);
  useEffect(() => {
    const normalized = normalizeTargetValue(targetValue);
    if (normalized !== lastAnalyzedTargetRef.current) {
      lastAnalyzedTargetRef.current = null;
      scopeAdviceMutation.reset();
    }
    // Only targetValue should re-run this — scopeAdviceMutation is stable across renders and
    // including it would re-trigger on every mutation state change (isPending/data/...).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetValue]);

  const handleTargetBlur = () => {
    if (!aiEnabled) return;
    const normalized = normalizeTargetValue(targetValue);
    // Same "long enough to be worth a live call" floor as the existing-target lookup effect above.
    if (normalized.length < 3 || normalized === lastAnalyzedTargetRef.current) return;
    lastAnalyzedTargetRef.current = normalized;
    scopeAdviceMutation.mutate();
  };

  const handleSelectRecommendedProfile = (recommended: ScanProfile) => {
    setProfile(recommended);
    toast({ title: `${PROFILE_LABEL[recommended]} selected`, description: 'Applied to Step 3 — Choose a scan profile.', variant: 'success' });
  };

  const handleDocumentSelected = (file: File | null) => {
    if (!file) {
      setDocumentBase64(null);
      setDocumentFileName(null);
      return;
    }
    setDocumentFileName(file.name);
    readFileAsBase64(file).then(setDocumentBase64);
  };

  const toggleCustomModule = (id: string) => {
    setCustomModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFinish = async () => {
    // Endpoint Hardening doesn't scan the target entered above at all — it runs via whatever
    // endpoint agents are already installed (apps/agent's daily check loop, not a dispatched run).
    // Rather than fabricating a fake run for something that doesn't work that way, this profile
    // just deep-links to the page where that actually lives.
    if (profile === 'endpointHardening') {
      navigate('/devices?tab=endpoints');
      return;
    }
    if (!authorizedBy) {
      setError("Couldn't resolve your account details — try refreshing the page.");
      return;
    }
    setError(null);
    setStarting(true);
    try {
      const { runId } = await runQuickScan({
        targetValue: normalizeTargetValue(targetValue),
        targetKind: kind,
        existingTarget,
        notBefore,
        notAfter,
        authorizedBy,
        profile,
        ...(documentBase64 ? { consentDocumentBase64: documentBase64 } : {}),
        ...(isInternalTarget && selectedAgentId ? { internalAgentId: selectedAgentId } : {}),
      });
      navigate(`/runs/${runId}`);
    } catch (err) {
      if (err instanceof QuickScanStepError) {
        setError(`${STEP_ERROR_MESSAGE[err.step]} ${friendlyErrorMessage(err.cause)}`);
      } else {
        setError(friendlyErrorMessage(err));
      }
    } finally {
      setStarting(false);
    }
  };

  const steps: WizardStep[] = [
    {
      key: 'target',
      title: 'What do you want to scan?',
      isValid: () => {
        if (normalizeTargetValue(targetValue).length === 0 || !authorizedSelfAttest) return false;
        // An internal target is blocked from proceeding until a connected agent is selected
        // (auto-selected when exactly one is connected) — no connected agent means no way to
        // actually reach it.
        if (isInternalTarget && (internalAgentsQuery.isLoading || !selectedAgentId)) return false;
        return true;
      },
      content: (
        <TargetStep
          value={targetValue}
          onChange={setTargetValue}
          onBlurTarget={handleTargetBlur}
          existingTarget={existingTarget}
          lookupPending={lookupPending}
          authorizedSelfAttest={authorizedSelfAttest}
          setAuthorizedSelfAttest={setAuthorizedSelfAttest}
          isInternalTarget={isInternalTarget}
          agentsLoading={internalAgentsQuery.isLoading}
          connectedAgents={connectedAgents}
          selectedAgentId={selectedAgentId}
          setSelectedAgentId={setSelectedAgentId}
          scopeAdvice={{ isPending: scopeAdviceMutation.isPending, data: scopeAdviceMutation.data }}
          onSelectRecommendedProfile={handleSelectRecommendedProfile}
        />
      ),
    },
    {
      key: 'authorize',
      title: 'Authorize this test',
      isValid: () =>
        confirmAuthorized &&
        acceptTerms &&
        acknowledgeLiability &&
        Boolean(notBefore) &&
        Boolean(notAfter) &&
        notAfter >= notBefore,
      content: (
        <AuthorizeStep
          targetValue={normalizeTargetValue(targetValue)}
          confirmAuthorized={confirmAuthorized}
          setConfirmAuthorized={setConfirmAuthorized}
          acceptTerms={acceptTerms}
          setAcceptTerms={setAcceptTerms}
          acknowledgeLiability={acknowledgeLiability}
          setAcknowledgeLiability={setAcknowledgeLiability}
          notBefore={notBefore}
          setNotBefore={setNotBefore}
          notAfter={notAfter}
          setNotAfter={setNotAfter}
          documentFileName={documentFileName}
          onDocumentSelected={handleDocumentSelected}
        />
      ),
    },
    {
      key: 'profile',
      title: 'Choose a scan profile',
      content: (
        <ProfileStep
          profile={profile}
          setProfile={setProfile}
          customModuleIds={customModuleIds}
          toggleCustomModule={toggleCustomModule}
          internalRedTeamAvailable={internalRedTeamAvailable}
        />
      ),
    },
  ];

  return (
    // No max-width here (was `mx-auto max-w-2xl`) — the profile step's card grid needs the full
    // page content width (see ProfileStep). Step 1 (TargetStep) and Step 2 (AuthorizeStep) each
    // keep their own `max-w-2xl` wrapper internally instead, so their actual form fields render
    // at exactly the same width as before; only the surrounding wizard shell (step-indicator,
    // step title, Back/Next buttons) is now wider for every step, not just this one.
    <div>
      <PageHeader title="New scan" description="Verify, authorize, and start testing a target in a few steps." />
      {error ? (
        <p role="alert" className="mb-4 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div className="rounded-2xl border border-border bg-surface-raised p-6">
        <Wizard
          steps={steps}
          onCancel={() => navigate('/')}
          onFinish={handleFinish}
          finishLabel={profile === 'endpointHardening' ? 'Go to Devices' : 'Start Scan'}
          finishing={starting}
        />
      </div>
    </div>
  );
}
